import { useState } from "react";
import { apiClient, ORDERABLE_SERVICES, INTELLIGENCE_SERVICES, VERIFICATION_SERVICES, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Check, Plus, ClipboardList } from "lucide-react";
import { toast } from "sonner";

/**
 * ModifyOrderDialog — extends an open application with additional services.
 *
 * Product principle: the application is a *living case*. Adding a service
 * updates the same screening record — order_history[] gets a new event,
 * reports[] grows a new pending row, and workflow/queue/findings/gate all
 * pick it up automatically because they are order-driven.
 *
 * Rules (mirrored in the backend at POST /api/screenings/{sid}/order/add-services):
 *  • PM (own screening) OR NTN Staff may open the dialog.
 *  • Blocked once a Final Decision is on file — button hidden by caller.
 *  • Services already on the order are shown as "In current order" and
 *    are not selectable.
 */
export default function ModifyOrderDialog({ screening, disabled, onModified, testidPrefix = "modify-order" }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const currentServices = new Set(screening.services || []);
  const availableToAdd = ORDERABLE_SERVICES.filter((r) => !currentServices.has(r.key));

  const toggle = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
  };

  const submit = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one service to add.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/screenings/${screening.id}/order/add-services`, {
        services: selected,
        reason: reason.trim() || null,
      });
      toast.success(`Added ${selected.length} service${selected.length !== 1 ? "s" : ""} to the order.`);
      setOpen(false);
      setSelected([]);
      setReason("");
      onModified?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add services.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSelected([]); setReason(""); } }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          data-testid={`${testidPrefix}-open`}
          className="rounded-sm border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white gap-1.5"
        >
          <ClipboardList className="h-3.5 w-3.5" /> Modify Order
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Modify Order</DialogTitle>
          <DialogDescription>
            Add screening services to this order. NTN staff are notified and will begin
            processing the added services. Pricing follows your account agreement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          {/* Current order */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-2">Current Order</div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              {(screening.services || []).length === 0 ? (
                <div className="text-xs text-slate-500">No services on this order yet.</div>
              ) : (
                <ul className="space-y-1.5" data-testid={`${testidPrefix}-current-order`}>
                  {ORDERABLE_SERVICES.filter((r) => currentServices.has(r.key)).map((r) => (
                    <li key={r.key} className="flex items-center gap-2 text-sm text-slate-800">
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" strokeWidth={3} />
                      <span className="font-medium">{r.label}</span>
                      <span className="text-[10px] font-mono tracking-wider uppercase text-slate-400 ml-auto">In current order</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Available to add */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-2">Available Additional Services</div>
            {availableToAdd.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid={`${testidPrefix}-nothing-to-add`}>
                All available services are already on this order.
              </div>
            ) : (
              <div className="space-y-4">
                <ServiceGroup title="Screening Intelligence" services={INTELLIGENCE_SERVICES} available={availableToAdd} selected={selected} onToggle={toggle} testidPrefix={`${testidPrefix}-intel`} />
                <ServiceGroup title="Verification Services" services={VERIFICATION_SERVICES} available={availableToAdd} selected={selected} onToggle={toggle} testidPrefix={`${testidPrefix}-verif`} />
              </div>
            )}
          </div>

          {availableToAdd.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                Reason <span className="text-slate-400">· optional</span>
              </Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional context — logged to the Case Communication Log."
                className="mt-1.5 rounded-sm"
                data-testid={`${testidPrefix}-reason`}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || selected.length === 0 || availableToAdd.length === 0}
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm gap-1.5"
            data-testid={`${testidPrefix}-submit`}
          >
            <Plus className="h-4 w-4" />
            {busy ? "Adding…" : `Add ${selected.length || ""} service${selected.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServiceGroup({ title, services, available, selected, onToggle, testidPrefix }) {
  const availableKeys = new Set(available.map((r) => r.key));
  const items = services.filter((s) => availableKeys.has(s.key));
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">{title}</div>
      <div className="grid sm:grid-cols-3 gap-2">
        {items.map((r) => {
          const on = selected.includes(r.key);
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onToggle(r.key)}
              data-testid={`${testidPrefix}-${r.key}`}
              className={`text-left rounded-md border p-2.5 transition-all ${
                on
                  ? "border-slate-900 bg-slate-900 text-white ring-1 ring-slate-900"
                  : "border-slate-200 bg-white hover:border-slate-400 text-slate-900"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`h-4 w-4 rounded-sm flex items-center justify-center shrink-0 ${on ? "bg-white text-slate-900" : "border border-slate-300"}`}>
                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                </div>
                <span className="font-medium text-sm">{r.label}</span>
              </div>
              <p className={`text-[11px] mt-1 leading-snug ${on ? "text-white/70" : "text-slate-500"}`}>{r.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
